import { useEffect, useMemo, useState } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { useNavigate } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FolderX,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { api } from "@/lib/convex-api";
import { organizationRoute } from "@/lib/app-routes";
import { formatUserFacingError } from "@/lib/errorMessages";
import { toast } from "sonner";
import { TeamManagement } from "@/features/settings/team-management";
import {
  canProjectConfigurePublishMode,
  getProjectGithubRepoUrl,
  getProjectRemoteLabel,
  getProjectSourceLabel,
  isLocalProject,
} from "@/features/project-source/source-helpers";
import type { Id } from "../../../../backend/convex/_generated/dataModel";
import { useAuthToken } from "@convex-dev/auth/react";

interface ProjectSettingsProps {
  projectId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export const ProjectSettings = ({
  projectId,
  open,
  onOpenChange,
  onDeleted,
}: ProjectSettingsProps) => {
  const navigate = useNavigate();
  const token = useAuthToken();
  const typedProjectId = projectId as Id<"projects"> | null;

  const project = useQuery(
    api.projects.getWithInstallation,
    typedProjectId ? { id: typedProjectId } : "skip",
  );
  const publishMode = useQuery(
    api.projectRemote.getPublishMode,
    typedProjectId ? { projectId: typedProjectId } : "skip",
  );
  const memberCount = useQuery(
    api.projects.getMemberCount,
    typedProjectId ? { projectId: typedProjectId } : "skip",
  );
  const files = useQuery(
    api.projectFiles.list,
    typedProjectId
      ? {
          projectId: typedProjectId,
        }
      : "skip",
  );
  const indexStats = useQuery(
    api.semanticSearch.getProjectIndexStats,
    typedProjectId
      ? {
          projectId: typedProjectId,
        }
      : "skip",
  );

  const updateProjectName = useMutation(api.projects.updateName);
  const updateExcludedFolders = useMutation(api.projects.updateExcludedFolders);
  const deleteProject = useMutation(api.projects.deleteProject);
  const scheduleProjectReindex = useMutation(
    api.semanticSearch.scheduleProjectReindex,
  );
  const setPublishMode = useAction(api.projectRemote.setPublishMode);

  const [projectName, setProjectName] = useState("");
  const [initialProjectName, setInitialProjectName] = useState("");
  const [strategy, setStrategy] = useState("direct");
  const [excludedFolders, setExcludedFolders] = useState<
    Array<{ folder: string; auto: boolean }>
  >([]);
  const [newFolder, setNewFolder] = useState("");
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");
  const [isTeamExpanded, setIsTeamExpanded] = useState(false);
  const [isReindexing, setIsReindexing] = useState(false);

  useEffect(() => {
    if (!project) return;
    setProjectName(project.name);
    setInitialProjectName(project.name);
    setExcludedFolders(
      project.excludedFolders.map((folder) => ({
        folder,
        auto:
          folder === "node_modules" ||
          folder === ".git" ||
          folder === "dist" ||
          folder === "build",
      })),
    );
  }, [project]);

  useEffect(() => {
    if (!publishMode) return;
    setStrategy(publishMode === "CHANGE_REQUEST" ? "pr" : "direct");
  }, [publishMode]);

  const projectFileCount = useMemo(() => files?.length ?? 0, [files]);
  const indexedFileCount = indexStats?.indexedFileCount ?? 0;
  const indexedChunkCount = indexStats?.indexedChunkCount ?? 0;
  const isLocal = project ? isLocalProject(project) : false;
  const sourceLabel = project ? getProjectSourceLabel(project) : "Loading...";
  const remoteLabel = project ? getProjectRemoteLabel(project) : null;
  const githubRepoUrl = project ? getProjectGithubRepoUrl(project) : null;
  const showPublishModeSettings = project
    ? canProjectConfigurePublishMode(project)
    : false;

  const handleSaveProjectName = async () => {
    const trimmed = projectName.trim();
    if (!typedProjectId) return;
    if (!trimmed || trimmed === initialProjectName) return;
    try {
      await updateProjectName({
        projectId: typedProjectId,
        name: trimmed,
      });
      setProjectName(trimmed);
      setInitialProjectName(trimmed);
    } catch (error) {
      toast.error(formatUserFacingError(error, "Failed to update name."));
      setProjectName(initialProjectName);
    }
  };

  const persistExcludedFolders = async (
    nextFolders: Array<{ folder: string; auto: boolean }>,
  ) => {
    if (!typedProjectId) return;
    await updateExcludedFolders({
      projectId: typedProjectId,
      excludedFolders: nextFolders.map((f) => f.folder),
    });
  };

  const handleRemoveFolder = async (folder: string) => {
    const nextFolders = excludedFolders.filter((f) => f.folder !== folder);
    setExcludedFolders(nextFolders);
    try {
      await persistExcludedFolders(nextFolders);
    } catch (error) {
      toast.error(
        formatUserFacingError(error, "Failed to update excluded folders."),
      );
      setExcludedFolders(excludedFolders);
    }
  };

  const handleAddFolder = async () => {
    const trimmed = newFolder.trim();
    if (trimmed && !excludedFolders.some((f) => f.folder === trimmed)) {
      const nextFolders = [
        ...excludedFolders,
        { folder: trimmed, auto: false },
      ];
      setExcludedFolders(nextFolders);
      try {
        await persistExcludedFolders(nextFolders);
        setNewFolder("");
        setShowAddFolder(false);
      } catch (error) {
        toast.error(
          formatUserFacingError(error, "Failed to update excluded folders."),
        );
        setExcludedFolders(excludedFolders);
      }
    }
  };

  const handleStrategyChange = async (value: string) => {
    if (!value || !typedProjectId || !token) return;
    const mode = value === "pr" ? "CHANGE_REQUEST" : "DIRECT";
    const prev = strategy;
    setStrategy(value);
    try {
      await setPublishMode({
        projectId: typedProjectId,
        token,
        mode,
      });
      toast.success("Sync strategy updated.");
    } catch (error) {
      setStrategy(prev);
      toast.error(formatUserFacingError(error, "Failed to update strategy."));
    }
  };

  const handleDeleteProject = async () => {
    if (!typedProjectId || !project) return;
    try {
      await deleteProject({ projectId: typedProjectId });
      onOpenChange(false);
      if (onDeleted) {
        onDeleted();
      } else {
        void navigate({
          to: organizationRoute,
          params: { orgSlug: project.organizationSlug },
        });
      }
    } catch (error) {
      toast.error(formatUserFacingError(error, "Failed to delete project."));
    }
  };

  const handleReindexProject = async () => {
    if (!typedProjectId || isReindexing) return;
    setIsReindexing(true);
    try {
      const result = await scheduleProjectReindex({
        projectId: typedProjectId,
      });
      toast.success("Semantic reindex started.", {
        description:
          result.scheduledFileCount > 0
            ? `Queued ${result.scheduledFileCount} files for reindexing.`
            : "No files were queued.",
      });
    } catch (error) {
      toast.error(
        formatUserFacingError(error, "Failed to start semantic reindex."),
      );
    } finally {
      setIsReindexing(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Project Settings"
    >
      <div className="space-y-5">
        {/* General */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">General</h3>
          <div className="space-y-2">
            <Label htmlFor="project-name">Project name</Label>
            <Input
              id="project-name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              onBlur={() => void handleSaveProjectName()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.currentTarget.blur();
                }
              }}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs font-normal">
              {isLocal ? "Provider" : "Repository"}
            </Label>
            <div className="flex items-center gap-1.5 text-sm text-foreground">
              {!isLocal ? (
                <>
                  <span>{remoteLabel ?? "Remote unavailable"}</span>
                  {githubRepoUrl ? (
                    <a
                      href={githubRepoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <ExternalLink className="size-3" />
                    </a>
                  ) : null}
                </>
              ) : (
                <span>{sourceLabel}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
            <span>
              <span className="text-muted-foreground text-xs font-normal">
                Root folder{" "}
              </span>
              <span className="text-foreground">
                {project?.rootFolder ?? "Loading..."}
              </span>
            </span>
            {!isLocal ? (
              <>
                <span className="text-muted-foreground/50">·</span>
                <span>
                  <span className="text-muted-foreground text-xs font-normal">
                    Branch{" "}
                  </span>
                  <span className="text-foreground">
                    {project?.branch ?? "HEAD"}
                  </span>
                </span>
              </>
            ) : null}
          </div>
        </section>

        {showPublishModeSettings ? (
          <>
            <Separator />

            {/* Sync & Commit */}
            <section className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">
                Sync &amp; Commit Strategy
              </h3>
              <div className="space-y-2">
                <ToggleGroup
                  type="single"
                  variant="outline"
                  value={strategy}
                  onValueChange={(v) => void handleStrategyChange(v)}
                >
                  <ToggleGroupItem value="direct" className="text-xs">
                    Direct Publish
                  </ToggleGroupItem>
                  <ToggleGroupItem value="pr" className="text-xs">
                    Review Request
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
              <p className="text-xs text-muted-foreground">
                {sourceLabel} projects can publish directly or route changes
                through a review request when direct publishing is blocked.
              </p>
            </section>
          </>
        ) : null}

        <Separator />

        {/* Excluded Folders */}
        <section className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">
            Excluded Folders
          </h3>
          <p className="text-xs text-muted-foreground">
            {!isLocal
              ? "Excluded folders are not synced or indexed. Changes take effect on the next sync."
              : "Excluded folders are not indexed for this cloud project."}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {excludedFolders.map(({ folder, auto }) => (
              <span
                key={folder}
                className="inline-flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-0.5 text-xs text-foreground"
              >
                <FolderX className="size-3 text-muted-foreground" />
                {folder}
                {auto && <span className="text-muted-foreground">(auto)</span>}
                <button
                  type="button"
                  onClick={() => handleRemoveFolder(folder)}
                  className="ml-0.5 rounded-sm hover:bg-muted"
                >
                  <X className="size-3 text-muted-foreground" />
                </button>
              </span>
            ))}
          </div>
          {showAddFolder ? (
            <div className="flex items-center gap-2">
              <Input
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddFolder();
                  if (e.key === "Escape") setShowAddFolder(false);
                }}
                placeholder="Folder name"
                className="h-7 text-xs"
                autoFocus
              />
              <Button size="xs" variant="secondary" onClick={handleAddFolder}>
                Add
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={() => setShowAddFolder(true)}
            >
              <Plus className="size-3" />
              Add folder
            </Button>
          )}
          <div className="space-y-2 rounded-md border bg-muted/20 p-3">
            <div className="space-y-1">
              <h4 className="text-xs font-medium text-foreground">
                Semantic Search Index
              </h4>
              <p className="text-xs text-muted-foreground">
                Indexed files: {indexedFileCount} / {projectFileCount}
              </p>
              <p className="text-xs text-muted-foreground">
                Indexed chunks: {indexedChunkCount}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="text-xs"
              onClick={() => void handleReindexProject()}
              disabled={isReindexing || !typedProjectId}
            >
              {isReindexing ? (
                <Loader2 className="size-3 animate-spin" />
              ) : null}
              Rebuild semantic index
            </Button>
            <p className="text-xs text-muted-foreground">
              Requeues all current project files for semantic indexing without
              forcing a full resync.
            </p>
          </div>
        </section>

        <Separator />

        {/* Team — collapsible section */}
        <section className="space-y-3">
          <button
            type="button"
            className="flex w-full items-center gap-2"
            onClick={() => setIsTeamExpanded((prev) => !prev)}
          >
            {isTeamExpanded ? (
              <ChevronDown className="size-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="size-3.5 text-muted-foreground" />
            )}
            <h3 className="text-sm font-medium text-foreground">Team</h3>
            <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
              {memberCount ?? 0} members
            </span>
          </button>
          {isTeamExpanded && typedProjectId && (
            <TeamManagement projectId={typedProjectId} />
          )}
        </section>

        <Separator />

        {/* Danger Zone */}
        <section className="space-y-3">
          <div className="rounded-md border border-destructive/30 p-3 space-y-3">
            <h3 className="text-sm font-medium text-destructive">
              Danger Zone
            </h3>
            {showDeleteConfirm ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Type{" "}
                  <span className="font-mono font-medium text-foreground">
                    {project?.name ?? projectName}
                  </span>{" "}
                  to confirm deletion.
                </p>
                <Input
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder={project?.name ?? projectName}
                  className="h-8 text-sm"
                />
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteInput !== (project?.name ?? projectName)}
                    onClick={() => void handleDeleteProject()}
                  >
                    <Trash2 className="size-3" />
                    Confirm Delete
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteInput("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="size-3" />
                Delete Project
              </Button>
            )}
          </div>
        </section>
      </div>
    </ResponsiveDialog>
  );
};
